import { CalendarClock, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const AUTO_DISMISS_MS = 8000;

interface ReassignmentDateBumpNoticeProps {
  /** The new due date that was set (ISO string) */
  newDueDate: string;
  /** The assignee name for the message */
  assigneeName?: string;
  /** Why the bump happened */
  reason: 'overdue' | 'late_today';
}

export const ReassignmentDateBumpNotice: React.FC<ReassignmentDateBumpNoticeProps> = ({
  newDueDate,
  assigneeName,
  reason,
}) => {
  const [visible, setVisible] = useState(true);
  const [paused, setPaused] = useState(false);
  const remainingRef = useRef(AUTO_DISMISS_MS);
  const startTimeRef = useRef(Date.now());
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(() => setVisible(false), remainingRef.current);
  }, []);

  const pauseTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    remainingRef.current -= Date.now() - startTimeRef.current;
    if (remainingRef.current < 0) remainingRef.current = 0;
  }, []);

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [startTimer]);

  const handleMouseEnter = useCallback(() => {
    setPaused(true);
    pauseTimer();
    if (barRef.current) {
      const computed = getComputedStyle(barRef.current);
      barRef.current.style.animationPlayState = 'paused';
      barRef.current.style.width = computed.width;
    }
  }, [pauseTimer]);

  const handleMouseLeave = useCallback(() => {
    setPaused(false);
    startTimer();
    if (barRef.current) {
      barRef.current.style.animationPlayState = 'running';
    }
  }, [startTimer]);

  const handleDismiss = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setVisible(false);
  }, []);

  if (!visible) return null;

  const date = new Date(newDueDate);
  const dateLabel = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <div
      className="overflow-hidden rounded-lg bg-amber-50 border border-amber-200 animate-in fade-in slide-in-from-top-1 duration-200"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-start gap-2 px-3 py-2 text-xs">
        <CalendarClock className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-amber-900">
            Next action date moved to {dateLabel}
          </p>
          <p className="font-medium text-amber-700 mt-0.5">
            {reason === 'overdue'
              ? 'This task was overdue — date updated on reassignment.'
              : `Reassigned after 3 PM EST.${assigneeName ? ` If this must be done today, message ${assigneeName} directly.` : ' If this must be done today, message the assignee directly.'}`}
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="text-amber-400 hover:text-amber-600 transition-colors shrink-0"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {/* Countdown progress bar */}
      <div className="h-1 w-full bg-amber-100">
        <div
          ref={barRef}
          className="h-full bg-amber-400 rounded-r-full"
          style={{
            animation: `shrinkBar ${AUTO_DISMISS_MS}ms linear forwards`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
        />
        <style>{`
          @keyframes shrinkBar {
            from { width: 100%; }
            to { width: 0%; }
          }
        `}</style>
      </div>
    </div>
  );
};
